import React, { useState, useEffect, useRef } from "react";
import { UpdateAppModal } from "./components/Modals";
import { manageErrorLogs, notify } from "./helpers/commonFunction";
import { toastMessages } from "./helpers/messages";
import { routes } from "./helpers/constants";

const ServiceWWrapper = () => {
  const intervalRef = useRef(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      const registerServiceWorker = async () => {
        const swURL = `${process.env.PUBLIC_URL}/sw.js`;
        try {
          const reg = await navigator.serviceWorker.register(swURL);
          setRegistration(reg);

          if (localStorage.getItem("app_update_pending") === "true") {
            localStorage.removeItem("app_update_pending");
            return;
          }

          if (reg.waiting) {
            setShowPopup(true);
          }

          reg.addEventListener("updatefound", () => {
            const installingWorker = reg.installing;
            if (installingWorker) {
              installingWorker.addEventListener("statechange", () => {
                if (
                  installingWorker.state === "installed" &&
                  navigator.serviceWorker.controller
                ) {
                  setShowPopup(true);
                }
              });
            }
          });

          const controller = navigator.serviceWorker.controller;
          if (controller) {
            sendNetworkHint(controller);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            checkVersion(controller);
            intervalRef.current = setInterval(() => {
              if (navigator.serviceWorker.controller) {
                checkVersion(navigator.serviceWorker.controller);
                sendNetworkHint(navigator.serviceWorker.controller);
              }
            }, 60000);
          }
        } catch (error) {
          manageErrorLogs(error);
        }
      };
      registerServiceWorker();

      // Listen for cache status messages
      const handleMessage = (event) => {
        if (event.data?.type === "CACHE_STATUS") {
          const { completed } = event.data;
          if (completed) {
            localStorage.setItem("offline_ready", "1");
            if (
              window?.navigator?.onLine &&
              window?.location?.pathname !== routes.login &&
              !localStorage.getItem("offline_mode_notification_shown")
            ) {
              notify(toastMessages.offlineMode, "success");
              localStorage.setItem("offline_mode_notification_shown", "1");
            }
          } else {
            localStorage.setItem("offline_ready", "0");
          }
        }
      };
      navigator.serviceWorker.addEventListener("message", handleMessage);

      // Request initial cache status after SW is ready
      navigator.serviceWorker.ready.then((registration) => {
        setTimeout(() => {
          if (registration.active) {
            registration.active.postMessage({ type: "CHECK_CACHE_STATUS" });
          }
        }, 1000);
      });

      return () => {
        navigator.serviceWorker.removeEventListener("message", handleMessage);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, []);

  const sendNetworkHint = (controller) => {
    if ("connection" in navigator && controller) {
      const conn = navigator.connection;
      const isFast =
        conn.effectiveType === "4g" && conn.downlink >= 1.2 && conn.rtt < 900;
      controller.postMessage({
        type: "CLIENT_NETWORK_HINT",
        hint: isFast ? "fast" : "slow",
      });
    }
  };

  const checkVersion = async (controller) => {
    if (!controller) {
      return;
    }
    try {
      const response = await fetch(`/version.json`, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data?.version) {
        localStorage.setItem("app_version", data.version);
        const channel = new MessageChannel();
        channel.port1.onmessage = (event) => {
          if (event.data && event.data.type === "VERSION_UPDATE") {
            setShowPopup(true);
          }
        };
        controller.postMessage({ type: "CHECK_VERSION" }, [channel?.port2]);
      }
    } catch (error) {
      manageErrorLogs(error);
    }
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      localStorage.setItem("app_update_pending", "true");
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
        await new Promise((resolve) => {
          const checkActivation = () => {
            if (
              registration.active &&
              registration.active !== registration.waiting
            ) {
              resolve();
            } else {
              setTimeout(checkActivation, 100);
            }
          };
          checkActivation();
        });
      }
      localStorage.removeItem("offline_mode_notification_shown");
      window.location.reload(true);
    } catch (error) {
      setIsLoading(false);
      setShowPopup(false);
      manageErrorLogs(error);
    }
  };

  return (
    <>
      {showPopup && (
        <UpdateAppModal onRefresh={handleRefresh} isLoading={isLoading} />
      )}
    </>
  );
};

export default ServiceWWrapper;
